// Stock Transfers - Step 3: Review & Complete
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  CheckCircle2,
  ArrowRightLeft,
  Info,
  Package,
  MapPin,
  Calendar,
  FileText,
  User,
  Hash,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { TransferConfigData } from "./stock-transfers-step2";
import { format } from "date-fns";

interface StockTransfersStep3PageProps {
  pageParams?: {
    configData?: TransferConfigData;
  };
}

export function StockTransfersStep3Page({ pageParams }: StockTransfersStep3PageProps) {
  const { navigateTo } = useNavigation();
  const configData = pageParams?.configData;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if no config data
  useEffect(() => {
    if (!configData) {
      toast.error("Please complete previous steps first");
      navigateTo("stock-transfers/create/step1");
    }
  }, [configData, navigateTo]);

  if (!configData) {
    return null;
  }

  const handleBack = () => {
    navigateTo("stock-transfers/create/step2", { setupData: configData });
  };

  const handleSaveAsDraft = async () => {
    setIsSubmitting(true);
    try {
      // TODO: Save as draft API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Transfer saved as draft successfully!");
      navigateTo("stock-transfers");
    } catch (error) {
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteTransfer = async () => {
    setIsSubmitting(true);
    try {
      // TODO: Complete transfer API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      toast.success(
        <div>
          <div className="font-semibold">Transfer completed successfully!</div>
          <div className="text-sm mt-1">
            {configData.transfers.length} items transferred
          </div>
        </div>
      );
      navigateTo("stock-transfers");
    } catch (error) {
      toast.error("Failed to complete transfer. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate totals
  const totalQuantity = configData.transfers.reduce((sum, t) => sum + t.quantity, 0);
  const uniqueLocations = new Set([
    ...configData.transfers.map((t) => t.sourceLocation),
    ...configData.transfers.map((t) => t.destinationLocation),
  ]).size;

  return (
    <div className="flex flex-col h-full max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Review and complete your transfer
            </p>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
              ✓
            </div>
            <span className="text-sm text-muted-foreground">Select Items</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
              ✓
            </div>
            <span className="text-sm text-muted-foreground">Configure Transfers</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
              3
            </div>
            <span className="text-sm font-medium">Review & Complete</span>
          </div>
        </div>
      </div>

      {/* Success Banner */}
      <Card className="mb-6 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                Ready to complete transfer
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Review the details below and click "Complete Transfer" to process all {configData.transfers.length} transfers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{configData.transfers.length}</div>
                <div className="text-sm text-muted-foreground">Items</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <Hash className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{totalQuantity}</div>
                <div className="text-sm text-muted-foreground">Total Units</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/20">
                <MapPin className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold">{uniqueLocations}</div>
                <div className="text-sm text-muted-foreground">Locations</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transfer Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Transfer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>Reference</span>
              </div>
              <div className="font-medium">{configData.reference}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Transfer Date</span>
              </div>
              <div className="font-medium">
                {format(new Date(configData.date), "dd MMM yyyy")}
              </div>
            </div>

            {configData.notes && (
              <div className="space-y-1 md:col-span-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>Notes</span>
                </div>
                <div className="text-sm">{configData.notes}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Transfer Items */}
      <Card className="flex-1">
        <CardHeader>
          <CardTitle>Transfer Items</CardTitle>
          <CardDescription>
            Review all {configData.transfers.length} transfers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {configData.transfers.map((transfer, index) => (
              <Card key={transfer.itemId} className="border-2">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {/* Item Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="font-semibold text-lg">{transfer.itemCode}</div>
                          <div className="text-sm text-muted-foreground">
                            {transfer.itemName}
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-lg px-3 py-1">
                        {transfer.quantity} units
                      </Badge>
                    </div>

                    <Separator />

                    {/* Transfer Route */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>From</span>
                        </div>
                        <div className="font-medium text-sm">
                          {transfer.sourceLocation.split("-").map((part) => part.trim()).join(" - ")}
                        </div>
                      </div>

                      <div className="flex items-center justify-center">
                        <ArrowRightLeft className="h-5 w-5 text-muted-foreground" />
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>To</span>
                        </div>
                        <div className="font-medium text-sm">
                          {transfer.destinationLocation.split("-").map((part) => part.trim()).join(" - ")}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAsDraft}
            disabled={isSubmitting}
          >
            Save as Draft
          </Button>
          <Button
            onClick={handleCompleteTransfer}
            disabled={isSubmitting}
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Complete Transfer
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
